import { parseApiJson } from "./json";
import type {
  ApiErrorResponse,
  ApiViolation,
  CurrentUser,
  TokenResponse,
} from "./types";

export const BACKOFFICE_API = (
  import.meta.env.VITE_BACKOFFICE_API_URL || "/backoffice-api"
).replace(/\/+$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly error: string;
  readonly requestId?: string;
  readonly violations: ApiViolation[];

  constructor(status: number, payload: ApiErrorResponse = {}) {
    const violations = Array.isArray(payload.violations)
      ? payload.violations
      : [];
    super(
      payload.message ||
        violations
          .map(({ field, message }) => `${field}: ${message}`)
          .join("; ") ||
        payload.error?.replace(/_/g, " ") ||
        `Backoffice request failed (${status}).`,
    );
    this.name = "ApiError";
    this.status = status;
    this.error = payload.error || "request_failed";
    this.requestId = payload.requestId;
    this.violations = violations;
  }
}

export interface AuthSnapshot {
  authenticated: boolean;
  passwordChangeRequired: boolean;
  user: CurrentUser | null;
  identityLoading: boolean;
  identityError: Error | null;
  version: number;
}

type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;
export type ApiRequestOptions = RequestInit & {
  params?: QueryParams;
  json?: unknown;
};

let snapshot: AuthSnapshot = {
  authenticated: false,
  passwordChangeRequired: false,
  user: null,
  identityLoading: false,
  identityError: null,
  version: 0,
};
let tokens: TokenResponse | null = null;
let expiresAt = 0;
let generation = 0;
let refreshFlight: Promise<void> | null = null;
let identityFlight: Promise<CurrentUser> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

export const getSnapshot = () => snapshot;
export const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const authStore = { getSnapshot, subscribe };

function updateSnapshot(patch: Partial<Omit<AuthSnapshot, "version">>) {
  snapshot = { ...snapshot, ...patch, version: snapshot.version + 1 };
  listeners.forEach((listener) => listener());
}

function cancelRefreshTimer() {
  if (refreshTimer !== undefined) clearTimeout(refreshTimer);
  refreshTimer = undefined;
}

function clearSession() {
  generation += 1;
  cancelRefreshTimer();
  tokens = null;
  expiresAt = 0;
  refreshFlight = null;
  identityFlight = null;
  updateSnapshot({
    authenticated: false,
    passwordChangeRequired: false,
    user: null,
    identityLoading: false,
    identityError: null,
  });
}

function sessionChanged(): ApiError {
  return new ApiError(401, {
    error: "session_changed",
    message: "Your session changed. Please sign in again.",
  });
}

function assertGeneration(expected: number) {
  if (expected !== generation) throw sessionChanged();
}

function checkAbort(signal?: AbortSignal | null) {
  if (signal?.aborted) throw new DOMException("Request aborted", "AbortError");
}

export function buildQuery(params: QueryParams = {}): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([name, rawValue]) => {
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    values.forEach((value) => {
      if (value !== null && value !== undefined && value !== "") {
        query.append(name, String(value));
      }
    });
  });
  const result = query.toString();
  return result ? `?${result}` : "";
}

function apiUrl(path: string, params?: QueryParams): string {
  const pathname = path.split(/[?#]/, 1)[0];
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(pathname);
  } catch {
    throw new Error("Backoffice requests must use a valid API-relative path.");
  }
  if (
    !path.startsWith("/api/") ||
    decodedPath.includes("\\") ||
    decodedPath.split("/").some((part) => part === "." || part === "..")
  ) {
    throw new Error("Backoffice requests must use an API-relative path.");
  }
  const query = buildQuery(params);
  return `${BACKOFFICE_API}${path}${
    path.includes("?") && query ? `&${query.slice(1)}` : query
  }`;
}

async function send<T>(
  path: string,
  options: ApiRequestOptions = {},
  accessToken?: string,
): Promise<T> {
  const { params, json, headers: originalHeaders, ...request } = options;
  const headers = new Headers(originalHeaders);
  headers.set("Accept", "application/json");
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (json !== undefined) headers.set("Content-Type", "application/json");
  let response: Response;
  try {
    response = await fetch(apiUrl(path, params), {
      ...request,
      headers,
      body: json !== undefined ? JSON.stringify(json) : request.body,
      credentials: "omit",
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") throw error;
    throw new ApiError(0, {
      error: "network_error",
      message:
        "Cannot reach the Backoffice API. Check your connection and API address.",
    });
  }
  const text = await response.text();
  let result: unknown;
  try {
    result = text ? parseApiJson(text) : undefined;
  } catch {
    const corsRejected =
      response.status === 403 && text.trim() === "Invalid CORS request";
    const forbidden = response.status === 403;
    throw new ApiError(response.status, {
      error: corsRejected
        ? "cors_rejected"
        : forbidden
          ? "non_json_forbidden"
          : "invalid_response",
      message: corsRejected
        ? "The Backoffice API rejected the request origin (CORS). Check the development proxy or backend allowed origins."
        : forbidden
          ? "The Backoffice server rejected the request (HTTP 403) without a JSON error. Check the proxy and backend origin settings."
          : "The Backoffice API returned an unexpected response.",
      requestId: response.headers.get("x-request-id") || undefined,
    });
  }
  if (!response.ok) {
    const payload =
      result && typeof result === "object" ? (result as ApiErrorResponse) : {};
    throw new ApiError(response.status, {
      ...payload,
      requestId:
        payload.requestId || response.headers.get("x-request-id") || undefined,
    });
  }
  return result as T;
}

function handleAuthError(error: unknown, expected: number) {
  if (generation !== expected || !(error instanceof ApiError)) return;
  if (
    error.status === 401 &&
    [
      "unauthorized",
      "invalid_token",
      "invalid_refresh_token",
      "token_expired",
    ].includes(error.error)
  ) {
    clearSession();
  } else if (
    error.status === 403 &&
    error.error === "password_change_required"
  ) {
    cancelRefreshTimer();
    updateSnapshot({ passwordChangeRequired: true });
  }
}

function scheduleRefresh() {
  cancelRefreshTimer();
  if (!tokens || snapshot.passwordChangeRequired) return;
  const expected = generation;
  // Very short-lived tokens still get a bounded delay instead of a tight loop.
  const delay = Math.max(1000, expiresAt - Date.now() - 60_000);
  refreshTimer = setTimeout(() => {
    refreshTimer = undefined;
    if (generation !== expected || snapshot.passwordChangeRequired) return;
    void refreshSession().catch(() => {
      // Keep a valid session on transient failures; the next request retries.
      if (
        tokens &&
        generation === expected &&
        !snapshot.passwordChangeRequired
      ) {
        refreshTimer = setTimeout(() => {
          refreshTimer = undefined;
          if (generation === expected && !snapshot.passwordChangeRequired) {
            void refreshSession().catch(() => undefined);
          }
        }, 15_000);
      }
    });
  }, delay);
}

function saveTokens(value: TokenResponse) {
  if (
    !value ||
    typeof value.accessToken !== "string" ||
    !value.accessToken ||
    typeof value.refreshToken !== "string" ||
    !value.refreshToken ||
    value.tokenType !== "Bearer" ||
    !Number.isFinite(value.expiresIn) ||
    value.expiresIn <= 0 ||
    typeof value.passwordChangeRequired !== "boolean"
  ) {
    throw new ApiError(502, {
      error: "invalid_response",
      message: "The Backoffice API returned an invalid sign-in response.",
    });
  }
  tokens = value;
  expiresAt = Date.now() + Math.max(0, value.expiresIn) * 1000;
  updateSnapshot({
    authenticated: true,
    passwordChangeRequired: value.passwordChangeRequired,
  });
  scheduleRefresh();
}

async function refreshSession(): Promise<void> {
  if (refreshFlight) return refreshFlight;
  if (!tokens) throw sessionChanged();
  const expected = generation;
  const refreshToken = tokens.refreshToken;
  const flight = (async () => {
    try {
      const value = await send<TokenResponse>("/api/auth/refresh", {
        method: "POST",
        json: { refreshToken },
      });
      assertGeneration(expected);
      saveTokens(value);
    } catch (error) {
      handleAuthError(error, expected);
      throw error;
    }
  })();
  refreshFlight = flight;
  try {
    await flight;
  } finally {
    if (refreshFlight === flight) refreshFlight = null;
  }
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const expected = generation;
  checkAbort(options.signal);
  if (!tokens) throw sessionChanged();
  // Temporary credentials must be usable for the password endpoint even when
  // their remaining lifetime is shorter than the normal refresh headroom.
  if (
    expiresAt <= Date.now() ||
    (!snapshot.passwordChangeRequired && expiresAt <= Date.now() + 60_000)
  ) {
    await refreshSession();
  }
  checkAbort(options.signal);
  assertGeneration(expected);
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const accessToken: string | undefined = tokens?.accessToken;
    try {
      const result = await send<T>(path, options, accessToken);
      assertGeneration(expected);
      return result;
    } catch (error) {
      assertGeneration(expected);
      if (
        attempt === 0 &&
        error instanceof ApiError &&
        error.status === 401 &&
        error.error === "token_expired"
      ) {
        // A concurrent request may already have replaced this expired token.
        if (tokens?.accessToken === accessToken) await refreshSession();
        checkAbort(options.signal);
        assertGeneration(expected);
        continue;
      }
      handleAuthError(error, expected);
      throw error;
    }
  }
  throw sessionChanged();
}

export async function loadCurrentUser(): Promise<CurrentUser> {
  if (identityFlight) return identityFlight;
  const expected = generation;
  updateSnapshot({ identityLoading: true, identityError: null });
  const flight = (async () => {
    try {
      const user = await apiRequest<CurrentUser>("/api/auth/me");
      assertGeneration(expected);
      if (
        !user ||
        typeof user.userId !== "string" ||
        !user.userId ||
        typeof user.email !== "string" ||
        !["PLATFORM_ADMIN", "BRAND_ADMIN", "BRAND_VIEWER"].includes(user.role)
      ) {
        throw new ApiError(502, {
          error: "invalid_response",
          message: "The Backoffice API returned an invalid user profile.",
        });
      }
      updateSnapshot({ user, identityError: null });
      return user;
    } catch (error) {
      if (generation === expected && snapshot.authenticated) {
        updateSnapshot({
          identityError:
            error instanceof Error
              ? error
              : new Error("Cannot load your account."),
        });
      }
      throw error;
    } finally {
      if (generation === expected) updateSnapshot({ identityLoading: false });
    }
  })();
  identityFlight = flight;
  try {
    return await flight;
  } finally {
    if (identityFlight === flight) identityFlight = null;
  }
}

export async function login(
  email: string,
  password: string,
): Promise<TokenResponse> {
  clearSession();
  const expected = generation;
  try {
    const value = await send<TokenResponse>("/api/auth/login", {
      method: "POST",
      json: { email, password },
    });
    assertGeneration(expected);
    saveTokens(value);
    if (!value.passwordChangeRequired) {
      try {
        await loadCurrentUser();
      } catch (error) {
        assertGeneration(expected);
        // SessionGate blocks access and offers retry after a temporary /me failure.
        if (!snapshot.authenticated) throw error;
      }
    }
    return value;
  } catch (error) {
    if (generation === expected) scheduleRefresh();
    throw error;
  }
}

export async function logout(): Promise<void> {
  const refreshToken = tokens?.refreshToken;
  const accessToken = tokens?.accessToken;
  clearSession();
  if (refreshToken) {
    await send<void>(
      "/api/auth/logout",
      { method: "POST", json: { refreshToken } },
      accessToken,
    );
  }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<TokenResponse> {
  const expected = generation;
  const value = await apiRequest<TokenResponse>("/api/auth/password", {
    method: "POST",
    json: { currentPassword, newPassword },
  });
  assertGeneration(expected);
  // Fence refresh responses that were started with the previous credentials.
  generation += 1;
  refreshFlight = null;
  identityFlight = null;
  saveTokens(value);
  const updatedGeneration = generation;
  if (!value.passwordChangeRequired) {
    try {
      await loadCurrentUser();
    } catch (error) {
      assertGeneration(updatedGeneration);
      if (!snapshot.authenticated) throw error;
    }
  }
  return value;
}
